import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiZap, FiArrowRight, FiClock } from 'react-icons/fi';
import api from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';

const FlashDeals = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const { lang, t } = useLanguage();

  useEffect(() => {
    const fetchDeals = async () => {
      try {
        const res = await api.get('/flash-deals/active');
        setDeals(res.data.data || []);
      } catch { /* no deals */ }
      setLoading(false);
    };
    fetchDeals();
  }, [lang]);

  if (loading || deals.length === 0) return null;

  return (
    <section style={{ background: '#FEF2F2', padding: '3rem 0', position: 'relative', overflow: 'hidden' }}>
      {/* Animated background pulse */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: '4px',
        background: 'linear-gradient(90deg, #EF4444, #F59E0B, #EF4444, #F59E0B)',
        backgroundSize: '200% 100%',
        animation: 'flashSlide 2s linear infinite',
      }} />

      <style>{`
        @keyframes flashSlide { 0% { background-position: 0% 50%; } 100% { background-position: 200% 50%; } }
        @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
        .flash-product-card:hover { transform: translateY(-4px); box-shadow: 0 8px 24px rgba(239,68,68,0.15); }
      `}</style>

      <div className="container">
        {deals.map(deal => (
          <div key={deal._id}>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <span style={{
                  background: '#EF4444', color: '#FFF', borderRadius: '8px',
                  padding: '6px 14px', fontWeight: 800, fontSize: '15px',
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}>
                  <FiZap size={16} /> {t('flashDeal')}
                </span>
                <div>
                  <h4 style={{ fontWeight: 800, margin: 0, color: '#1F2937' }}>{deal.name}</h4>
                  {deal.description && (
                    <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>{deal.description}</p>
                  )}
                </div>
              </div>

              <CountdownTimer endDate={deal.endDate} />
            </div>

            {/* Discount banner */}
            <div style={{
              background: 'linear-gradient(135deg, #EF4444, #DC2626)', borderRadius: '12px',
              padding: '12px 24px', marginBottom: '20px', textAlign: 'center', color: '#FFF',
            }}>
              <span style={{ fontSize: '28px', fontWeight: 900 }}>{t('upTo')} {deal.discountPercent}% {t('off')}</span>
              <span style={{ fontSize: '14px', opacity: 0.8, marginLeft: '12px' }}>
                | {t('limitedTimeOnly')}
              </span>
            </div>

            {/* Products grid */}
            <div className="row g-3">
              {(deal.products || []).slice(0, 6).map(product => (
                <div key={product._id} className="col-6 col-md-4 col-lg-2">
                  <Link
                    to={`/products/${product.slug}`}
                    className="flash-product-card"
                    style={{
                      display: 'block', textDecoration: 'none', color: 'inherit',
                      background: '#FFF', borderRadius: '12px', overflow: 'hidden',
                      border: '1px solid #FEE2E2', transition: 'all 0.2s',
                    }}
                  >
                    {/* Image */}
                    <div style={{ position: 'relative', paddingBottom: '100%', background: '#F9FAFB' }}>
                      {product.images?.[0] ? (
                        <img
                          src={product.images[0].url}
                          alt={product.name}
                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://placehold.co/600x600/27AE60/ffffff?text=${encodeURIComponent(product.name?.split(' ').slice(0, 2).join('+') || 'Product')}`;
                          }}
                        />
                      ) : (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#D1D5DB' }}>
                          No Image
                        </div>
                      )}
                      {/* Discount badge */}
                      <span style={{
                        position: 'absolute', top: '8px', left: '8px',
                        background: '#EF4444', color: '#FFF', borderRadius: '6px',
                        padding: '2px 8px', fontSize: '11px', fontWeight: 800,
                      }}>
                        -{deal.discountPercent}%
                      </span>
                    </div>

                    {/* Info */}
                    <div style={{ padding: '10px' }}>
                      <p style={{
                        fontSize: '12px', fontWeight: 600, color: '#374151',
                        margin: '0 0 6px', lineHeight: 1.3,
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      }}>
                        {product.name}
                      </p>
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ fontWeight: 800, fontSize: '16px', color: '#EF4444' }}>
                          ${product.flashPrice?.toFixed(2)}
                        </span>
                        <span style={{ fontSize: '12px', color: '#9CA3AF', textDecoration: 'line-through' }}>
                          ${product.originalPrice?.toFixed(2)}
                        </span>
                      </div>
                      {product.ratings?.average > 0 && (
                        <div style={{ fontSize: '11px', color: '#F59E0B', marginTop: '2px' }}>
                          {'★'.repeat(Math.round(product.ratings.average))} ({product.ratings.count})
                        </div>
                      )}
                    </div>
                  </Link>
                </div>
              ))}
            </div>

            {/* View all link */}
            {deal.products?.length > 6 && (
              <div className="text-center mt-3">
                <Link to={`/flash-deals/${deal._id}`} className="btn btn-outline-danger">
                  View All Deals <FiArrowRight className="ms-1" />
                </Link>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

// ── Countdown Timer ──
const CountdownTimer = ({ endDate }) => {
  const [timeLeft, setTimeLeft] = useState(calcTimeLeft(endDate));
  const { t } = useLanguage();

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calcTimeLeft(endDate));
    }, 1000);
    return () => clearInterval(timer);
  }, [endDate]);

  if (timeLeft.expired) {
    return <span style={{ color: '#EF4444', fontWeight: 700, fontSize: '14px' }}>⏰ {t('dealEnded') || 'Deal Ended'}</span>;
  }

  const segments = [
    { value: timeLeft.hours, label: t('hrs') },
    { value: timeLeft.minutes, label: t('min') },
    { value: timeLeft.seconds, label: t('sec') },
  ];

  if (timeLeft.days > 0) {
    segments.unshift({ value: timeLeft.days, label: t('days') });
  }

  return (
    <div className="d-flex align-items-center gap-1">
      <FiClock size={16} color="#EF4444" className="me-1" />
      <span style={{ fontSize: '12px', fontWeight: 600, color: '#EF4444', marginRight: '4px' }}>{t('endsIn')}:</span>
      {segments.map((seg, i) => (
        <React.Fragment key={seg.label}>
          <div style={{
            background: '#1F2937', color: '#FFF', borderRadius: '6px',
            padding: '4px 8px', minWidth: '40px', textAlign: 'center',
          }}>
            <div style={{ fontWeight: 900, fontSize: '18px', lineHeight: 1 }}>
              {String(seg.value).padStart(2, '0')}
            </div>
            <div style={{ fontSize: '8px', fontWeight: 700, opacity: 0.6, letterSpacing: '1px' }}>
              {seg.label}
            </div>
          </div>
          {i < segments.length - 1 && (
            <span style={{ fontWeight: 900, color: '#EF4444', fontSize: '18px' }}>:</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

function calcTimeLeft(endDate) {
  const diff = new Date(endDate) - new Date();
  if (diff <= 0) return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
  return {
    expired: false,
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

export default FlashDeals;
